import React, {useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch,
        Typography} from "@mui/material";
import {Close} from "@mui/icons-material";

import {saveTrainingSetup} from "../../data/quiz";
import {Grey2, Grey5, Grey17} from "../../theme/styles";

// What being trained on this revision takes.
//
// THREE TOGGLES, AND EACH SAYS WHAT IT DOES. A switch labelled only "Require Retraining" leaves the
// reader to guess when retraining is triggered, and they will guess wrong -- so the sentence under each
// one is part of the control rather than decoration.
//
// PHASE 1 STORES ALL THREE AND ACTS ON ONE. Required Quiz is enforced now: a revision that requires one
// cannot be released without it. Acknowledgment and Retraining are consumed when taking a quiz is built,
// and the dialog does not pretend otherwise -- see the note at the foot.

type Flags = {
    TrainingRequired: boolean;
    TrainingRequireAcknowledgment: boolean;
    TrainingRequireRetraining: boolean;
    TrainingRequireQuiz: boolean;
};

function Row({title, description, checked, disabled, onChange, last}: {
    title: string; description: string; checked: boolean; disabled: boolean;
    onChange: (value: boolean) => void; last?: boolean;
}) {
    return (
        <div style={{display: "flex", flexDirection: "row", alignItems: "flex-start",
                     justifyContent: "space-between", gap: 24, padding: "18px 0",
                     borderBottom: last ? "none" : `1px solid ${Grey2}`}}>
            <div style={{minWidth: 0}}>
                <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>{title}</Typography>
                <Typography style={{fontSize: 13, color: Grey5, marginTop: 4}}>{description}</Typography>
            </div>
            <Switch checked={checked} disabled={disabled}
                    onChange={event => onChange(event.target.checked)}/>
        </div>
    );
}

export default function TrainingSetupDialog({open, onClose, sessionContext, objectRevisionUSID,
                                             current, editable, onSaved}: {
    open: boolean;
    onClose: () => void;
    sessionContext: any;
    objectRevisionUSID: string;
    current: Flags;
    editable: boolean;
    onSaved: () => void;
}) {
    const [flags, setFlags] = useState<Flags>(current);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // REOPENED MEANS RE-READ. Cancelling leaves the switches where somebody dragged them, and the next
    // open must show what is stored rather than what was abandoned.
    useEffect(() => {
        if (open) {
            setFlags(current);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const set = (key: keyof Flags) => (value: boolean) =>
        setFlags(previous => ({...previous, [key]: value}));

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveTrainingSetup(sessionContext, objectRevisionUSID, flags);
            onSaved();
            onClose();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not save that.");
        } finally {
            setSaving(false);
        }
    };

    const locked = !editable || saving;

    return (
        // THREE TOGGLES, MATCHING THE DESIGN. A fourth -- a master "Require Training" -- was added here
        // at first and was a mistake twice over: it is a property of the RECORD rather than of its
        // training, so it belongs on the Detail tab where it now lives; and gating the other three
        // behind it meant somebody opening this dialog found Required Quiz greyed out with nothing on
        // screen explaining why.
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle style={{display: "flex", alignItems: "center", justifyContent: "space-between",
                                 fontSize: 22, fontWeight: 700, color: Grey17}}>
                Training Setup
                <IconButton size="small" onClick={onClose}><Close fontSize="small"/></IconButton>
            </DialogTitle>
            <DialogContent>
                <Row title="Require Acknowledgment"
                     description="Users must explicitly confirm they have read the document."
                     checked={flags.TrainingRequireAcknowledgment}
                     disabled={locked}
                     onChange={set("TrainingRequireAcknowledgment")}/>
                <Row title="Require Retraining"
                     description="Reset training status when the document receives administrative updates."
                     checked={flags.TrainingRequireRetraining}
                     disabled={locked}
                     onChange={set("TrainingRequireRetraining")}/>
                <Row title="Required Quiz"
                     description="Users must pass a custom quiz to complete their training. This revision
                                  cannot be released until a quiz exists."
                     checked={flags.TrainingRequireQuiz}
                     disabled={locked}
                     onChange={set("TrainingRequireQuiz")} last/>

                {!editable ? (
                    <Typography style={{fontSize: 13, color: Grey5, marginTop: 16}}>
                        This record is released, so its training setup is fixed. Revise it to change this.
                    </Typography>
                ) : null}
                {error ? (
                    <Typography style={{fontSize: 13, color: "#B42318", marginTop: 16}}>{error}</Typography>
                ) : null}
            </DialogContent>
            <DialogActions style={{padding: "12px 24px 20px"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button onClick={save} variant="contained" disabled={locked}
                        sx={{textTransform: "none", borderRadius: "999px", paddingInline: 3}}>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
